import React from 'react';
import { Box, Alert, AlertTitle, IconButton, Collapse } from '@mui/material';
import { BiX, BiCheck } from 'react-icons/bi';
import { useMovePlanner } from '@hopdrive/feature-move-planner';
import { DealerMovePlannerContent, DealerMovePlannerProvider } from '@features/moveCreation/plannerMount';
import { Locations } from '@gql/base-types';
import { useOptimisticMoves } from '../context/OptimisticMoveContext';

interface PlanningContext {
  pickup: Locations;
  delivery: Locations;
  dateTime: Date;
  driverId?: number;
}

interface MoveCreationPanelContentProps {
  planningContext?: PlanningContext | null;
  onClose?: () => void;
  onSuccess?: (moveId: number) => void;
  onDispatchComplete?: (optimisticId: string, success: boolean) => void;
  onSubmitStart?: () => void;
  isVisible?: boolean;
}

interface MoveCreationContentWithAlertProps {
  onClose?: () => void;
  onDispatchComplete?: (optimisticId: string, success: boolean) => void;
  onSubmitStart?: () => void;
  isVisible?: boolean;
}

/**
 * Inner component that reads the planner through useMovePlanner()
 * Displays driver assignment alert and handles optimistic UI flow
 */
const MoveCreationContentWithAlert = ({
  onClose,
  onDispatchComplete,
  onSubmitStart,
  isVisible = true,
}: MoveCreationContentWithAlertProps) => {
  const context = useMovePlanner();
  const { addOptimisticMove, updateOptimisticMove, removeOptimisticMove } = useOptimisticMoves();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [optimisticId, setOptimisticId] = React.useState<string | null>(null);
  const [hasCreatedOptimistic, setHasCreatedOptimistic] = React.useState(false);

  // Extract values from context
  const assignToDriverId = context.assignToDriverId;
  const assignToDriverName = context.assignToDriverName;
  const setAssignToDriverId = context.setAssignToDriverId;
  const setAssignToDriverName = context.setAssignToDriverName;
  const isDispatching = context.isDispatching;
  const saving = context.saving;
  const pendingMoveId = context.pendingMoveId;  // Available as soon as move is created in DB
  const successMoveId = context.successMoveId;
  const isLite = context.isLite;
  const lane = context.lane;
  const time = context.time;

  // Create optimistic move when saving starts (only once)
  // For Lite tier: requires assignToDriverId (driver pre-selected)
  // For Standard tier: create with driverId: null (will show on first hypothetical driver row)
  // Note: the panel is NOT closed here. It only closes once the move is confirmed
  // created (see close-on-success effect below), so submit errors keep the form open.
  React.useEffect(() => {
    if (saving && !hasCreatedOptimistic && time) {
      // Clear planning overlays immediately
      onSubmitStart?.();

      // Create optimistic skeleton for both Lite and Standard tier
      // Lite tier: uses assignToDriverId (driver pre-selected)
      // Standard tier: uses driverId: null (will be shown on first hypothetical driver row)
      const id = addOptimisticMove({
        driverId: assignToDriverId || null,
        startTime: new Date(time),
        laneDurationSec: lane?.duration_sec || 3600, // Forward: dealer -> customer
        inverseLaneDurationSec: lane?.inverse?.duration_sec || lane?.duration_sec || 3600, // Inverse: customer -> dealer (for travel TO customer)
        status: 'creating',
        tier: isLite ? 'lite' : 'standard',
        driverName: assignToDriverName || undefined,
      });

      setOptimisticId(id);
      setHasCreatedOptimistic(true);
    }
  }, [saving, hasCreatedOptimistic, assignToDriverId, time, lane, isLite, assignToDriverName, addOptimisticMove, onSubmitStart]);

  // Close the panel only once the move is confirmed created in the DB.
  // Standard tier: successMoveId is set after insert.
  // Lite tier: pendingMoveId is set right after insert, before async dispatch.
  // On any submit failure, neither is set, so the panel stays open for editing.
  React.useEffect(() => {
    if (pendingMoveId || successMoveId) {
      onClose?.();
    }
  }, [pendingMoveId, successMoveId, onClose]);

  // Clean up after a failed/aborted submit. When saving transitions from true to
  // false without a created move, remove the phantom optimistic skeleton and reset
  // local state so the user can edit and resubmit cleanly.
  const prevSavingRef = React.useRef(false);
  React.useEffect(() => {
    const wasSaving = prevSavingRef.current;
    prevSavingRef.current = !!saving;

    if (wasSaving && !saving && !pendingMoveId && !successMoveId) {
      if (optimisticId) {
        removeOptimisticMove(optimisticId);
      }
      setOptimisticId(null);
      setHasCreatedOptimistic(false);
    }
  }, [saving, pendingMoveId, successMoveId, optimisticId, removeOptimisticMove]);

  // Update optimistic move when dispatching starts (Lite tier)
  React.useEffect(() => {
    if (isDispatching && optimisticId) {
      updateOptimisticMove(optimisticId, { status: 'offering' });
    }
  }, [isDispatching, optimisticId, updateOptimisticMove]);

  // Set actualMoveId as soon as pendingMoveId is available (before dispatch completes)
  // This allows the optimistic block to hide immediately when the real move appears in data
  // For Lite tier: pendingMoveId is available after move creation, before dispatch
  // For Standard tier: pendingMoveId may not be available, will use successMoveId instead
  React.useEffect(() => {
    if (pendingMoveId && optimisticId) {
      updateOptimisticMove(optimisticId, { actualMoveId: pendingMoveId });
    }
  }, [pendingMoveId, optimisticId, updateOptimisticMove]);

  // Handle success state
  // Update optimistic move with actual move ID for both Lite and Standard tier
  React.useEffect(() => {
    if (successMoveId) {
      setShowSuccess(true);

      // Update optimistic move with actual move ID (works for both Lite and Standard tier)
      if (optimisticId) {
        updateOptimisticMove(optimisticId, {
          actualMoveId: successMoveId,
          status: 'completed',
        });
      }

      // Signal dispatch complete
      onDispatchComplete?.(optimisticId || 'standard-move', true);
    }
  }, [successMoveId, optimisticId, updateOptimisticMove, onDispatchComplete]);

  const handleCancelAssignment = () => {
    setAssignToDriverId?.(null);
    setAssignToDriverName?.(null);
  };

  // If panel is not visible but we're still mounted (during dispatch), render nothing visible
  if (!isVisible) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Success Banner */}
      <Collapse in={showSuccess}>
        <Alert
          severity="success"
          icon={<BiCheck size={24} />}
          sx={{ mb: 2 }}
        >
          <AlertTitle>Move Created Successfully!</AlertTitle>
          The move has been created{isLite ? ' and offered to the driver' : ''}. Closing panel...
        </Alert>
      </Collapse>

      {/* Driver Assignment Alert */}
      {assignToDriverId && !showSuccess && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="cancel driver assignment"
              color="inherit"
              size="small"
              onClick={handleCancelAssignment}
            >
              <BiX />
            </IconButton>
          }
        >
          <AlertTitle>Auto-Assignment Enabled</AlertTitle>
          This move will be automatically offered to {assignToDriverName || 'the selected driver'}
        </Alert>
      )}

      {/* Move Planner Form */}
      <Box sx={{ opacity: showSuccess ? 0.5 : 1, pointerEvents: (saving || isDispatching || showSuccess) ? 'none' : 'auto' }}>
        <DealerMovePlannerContent />
      </Box>

      {/* Note: Full-screen overlay removed - optimistic skeleton shows progress on timeline instead */}
    </Box>
  );
};

const MoveCreationPanelContent = ({
  planningContext,
  onClose,
  onSuccess,
  onDispatchComplete,
  onSubmitStart,
  isVisible = true,
}: MoveCreationPanelContentProps) => {
  return (
    <Box sx={{ display: isVisible ? 'block' : 'none' }}>
      <DealerMovePlannerProvider state={undefined} planningContext={planningContext} onSuccess={onSuccess}>
        <MoveCreationContentWithAlert
          onClose={onClose}
          onDispatchComplete={onDispatchComplete}
          onSubmitStart={onSubmitStart}
          isVisible={isVisible}
        />
      </DealerMovePlannerProvider>
    </Box>
  );
};

export default MoveCreationPanelContent;
